import { debounce } from '@treasury/utils';
import { tmIf } from '@treasury/utils/lit-helpers';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { filterIcon } from '../../assets/icons';
import { TmBaseComponent } from '../../tm-base.component';
import './subcomponents/tm-list-header-search-button';
import './subcomponents/tm-list-header-search-field';
import { TmListHeaderEvent, TmSearchTermChangeEvent } from './tm-list-header.types';

export const tagName = 'tm-list-header';
@customElement(tagName)
export class TmListHeader extends TmBaseComponent {
    @property({ attribute: false })
    public headerTitle?: string;

    @property({ attribute: false })
    public filteringEnabled = true;

    @property({ attribute: true, type: Boolean, reflect: true })
    public wrapHeaderText = false;

    @property({ attribute: false })
    public filterButton = false;

    @property({ attribute: false })
    public searchTerm = '';

    @property({ attribute: false })
    public placeholder = 'Search';

    @state()
    private subtitleSlot = createRef<HTMLSlotElement>();

    @state()
    private shouldShowBottomSearch = false;

    private debouncedDispatchEvent = debounce((e: Event) => this.dispatchEvent(e), 250);

    protected render() {
        return html`
            <div class="w-full flex flex-wrap justify-end border-b pt-3 px-4 pb-2.5 bg-primary">
                <!-- Header bar top row -->
                <div class="flex basis-full items-center justify-between">
                    <!-- Header title -->
                    <div
                        ${tmIf(this.headerTitle)}
                        class=${classMap({
                            'mr-1': true,
                            'text-nowrap': !this.wrapHeaderText,
                        })}
                    >
                        <h2 class="text-lg font-medium">${this.headerTitle}</h2>
                        <slot ${ref(this.subtitleSlot)}></slot>
                    </div>

                    <!-- Filter bar -->
                    <div ${tmIf(this.filteringEnabled)} class="@container flex grow justify-end">
                        <!-- Inline search field -->
                        <div
                            ${tmIf(!this.shouldShowBottomSearch && !this.headerTitle)}
                            class=${classMap({
                                '@md:inline-block grow text-sm': true,
                                'max-w-[300px]': !!this.headerTitle,
                            })}
                        >
                            ${this.renderSearchField()}
                        </div>

                        <!-- Search button -->
                        <tm-list-header-search-button
                            ${tmIf(this.headerTitle)}
                            @show-search-field=${async (e: CustomEvent) => {
                                this.shouldShowBottomSearch = e.detail;
                                await this.updateComplete;
                                this.dispatchEvent(new CustomEvent('search-field-update-complete'));
                            }}
                        ></tm-list-header-search-button>

                        <!-- Filter button -->
                        <button
                            ${tmIf(this.filterButton)}
                            class="pt-2.5 pb-2 px-2.5 filter-button"
                            title="Filter"
                            @click=${() => this.dispatchEvent(new CustomEvent('filterButtonClick'))}
                        >
                            ${filterIcon}
                        </button>

                        <!-- Header end slot -->
                        <slot name="header-end"></slot>
                    </div>
                </div>

                <!-- Bottom search field (visibility handled out-of-band due to complexity) -->
                <div
                    ${tmIf(this.shouldShowBottomSearch)}
                    ?data-test=${true}
                    class="bottom-search-field grow max-w-[448px] pt-2"
                >
                    ${this.renderSearchField()}
                </div>
            </div>
        `;
    }

    private dispatchSearchTermChange() {
        const event: TmSearchTermChangeEvent = new CustomEvent(TmListHeaderEvent.SearchTermChange, {
            bubbles: true,
            composed: true,
            detail: {
                value: this.searchTerm,
            },
        });
        this.debouncedDispatchEvent(event);
    }

    private renderSearchField() {
        return html`<tm-list-header-search-field
            noOuterSpacing
            clearButton
            large
            @value-changed=${(e: CustomEvent) => {
                this.searchTerm = e.detail.value;
                this.dispatchSearchTermChange();
            }}
            placeholder=${this.placeholder}
            .initial=${this.searchTerm}
        >
        </tm-list-header-search-field>`;
    }

    public static get styles() {
        return [
            css`
                .bottom-search-field tm-text-field::part(component) {
                    padding-top: 0px;
                }
                .filter-button path {
                    fill: var(--secondary-text-color);
                }
            `,
        ];
    }
}
